import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Snackbar from '@material-ui/core/Snackbar';
import SnackbarContent from '@material-ui/core/SnackbarContent';
import IconButton from '@material-ui/core/IconButton';
import Icon from '@material-ui/core/Icon';

const SEVERITY_COLORS = {
  success: '#528e70',
  error: '#c0392b',
  warning: '#e0a800',
  info: '#2f6fa8',
};

const ToastContext = createContext(null);

// Singleton imperativo — permite chamar toast.error(...) fora de componentes (class components,
// callbacks de fetch/catch) sem precisar do hook useToast. Registrado pelo ToastProvider ao montar.
let activeInstance = null;

export const toast = {
  show: (message, severity) => activeInstance && activeInstance.show(message, severity),
  success: (message) => activeInstance && activeInstance.success(message),
  error: (message) => activeInstance && activeInstance.error(message),
  warning: (message) => activeInstance && activeInstance.warning(message),
  info: (message) => activeInstance && activeInstance.info(message),
};

// Substitui window.alert/confirm por feedback consistente: toast.success('...'), toast.error('...'), etc.
export const ToastProvider = ({ children }) => {
  const [toastState, setToastState] = useState(null);

  const show = useCallback((message, severity = 'info') => {
    setToastState({ message, severity, key: Date.now() });
  }, []);

  const api = useMemo(
    () => ({
      show,
      success: (message) => show(message, 'success'),
      error: (message) => show(message, 'error'),
      warning: (message) => show(message, 'warning'),
      info: (message) => show(message, 'info'),
    }),
    [show]
  );

  useEffect(() => {
    activeInstance = api;
    return () => {
      activeInstance = null;
    };
  }, [api]);

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setToastState(null);
  };

  return (
    <ToastContext.Provider value={api}>
      {children}
      <Snackbar
        key={toastState ? toastState.key : 'empty'}
        open={!!toastState}
        autoHideDuration={5000}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <SnackbarContent
          style={{
            backgroundColor: toastState ? SEVERITY_COLORS[toastState.severity] : undefined,
          }}
          message={toastState ? toastState.message : ''}
          action={
            <IconButton key="close" color="inherit" onClick={handleClose}>
              <Icon>close</Icon>
            </IconButton>
          }
        />
      </Snackbar>
    </ToastContext.Provider>
  );
};

ToastProvider.propTypes = {
  children: PropTypes.node,
};

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast deve ser usado dentro de um ToastProvider');
  }
  return ctx;
};
